import {Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy} from '@angular/core';
import {TileElemOptions, TileFolderElemSetup, TileMinimalElemSetup} from '../../../../../shared/models/tile-template/interfaces';
import {TilesService} from '../../../../../core/services/tiles.service';
import {ShadeToolsService} from '../../../../../core/services/shade-tools.service';
import {AlarmsService} from '../../../../../core/services/alarms.service';
import {TileHistoryService} from '../../../../../core/services/tile-history.service';
import {SuperFolderStructure} from '../../../../../shared/models/structure';
import {Tile, TileFolderDisplayMode, TileId, TileIdMap} from '../../../../../shared/models/tile';
import { map, takeUntil, startWith, catchError, switchMap} from 'rxjs/operators';
import {combineLatest, of, Subject} from 'rxjs';
import {SessionService} from '../../../../../core/services/session.service';
import {DEFAULT_DATA_POINT_FOLDER_VIEW_ITEM_PX_HEIGHT} from '../../../../../shared/models/tile-dimensions';
import {TileAlarmBadgeComponent} from '../../alarm-badge/tile-alarm-badge.component';
import {PopoverMenuComponent} from '../../../popover-menu/popover-menu.component';
import {SpinnerComponent} from '../../../spinner/spinner.component';
import {TileMinimalComponent} from './tile-minimal/tile-minimal.component';
import {PopoverTileComponent} from '../../../popover-tile/popover-tile.component';
import {NgStyle} from '@angular/common';
import {TileDataPointComponent} from './tile-data-point/tile-data-point.component';
import {TileFolderDrawerComponent} from './drawer/tile-folder-drawer.component';
import {AlarmEvent} from 'src/shared/models/alarm';
import {TileEditorComponent} from '../../../tile-editor/tile-editor.component';
import {OptionGroup} from '../../../../../shared/models/option';
import {TileIcon} from '../../../../../shared/models/tile-icon';
import {ConnectedPosition} from '@angular/cdk/overlay';
import {NgDestroyService} from '../../../../../core/services/ng-destroy.service';
import {PermissionsService} from 'src/core/services/permissions.service';
import {TileMenuOptionId} from '../../../../../shared/constants/menu-option-ids';
import {ObjectType} from 'src/shared/models/object.model';
import {Permission} from '../../../../../shared/models/fga/permission';
import {MotiChatService} from '../../../../../moti/services/moti-chat.service';
import {isValidTileColor} from '@shared/utils/color.util';
import {filterByMatchingIds} from '@shared/utils/tile-filter.util';

@Component({
  selector: 'motn-tile-folder',
  templateUrl: './tile-folder.component.html',
  styleUrls: ['./tile-folder.component.scss'],
  imports: [
    TileAlarmBadgeComponent,
    PopoverMenuComponent,
    SpinnerComponent,
    TileMinimalComponent,
    PopoverTileComponent,
    NgStyle,
    TileDataPointComponent,
    TileFolderDrawerComponent,
    TileEditorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [NgDestroyService],
})
export class TileFolderComponent implements OnInit, OnChanges {
  @Input() elemDef: TileFolderElemSetup;
  @ViewChild('dataPointsScrollableBox', {static: false}) private dataPointsScrollableBoxElRef: ElementRef;
  @Input()
  tile: Tile;

  @Input()
  matchingTileIds: Set<TileId> | null = null;
  alarmEvent: AlarmEvent;
  private currentAlarmEvents: AlarmEvent[] | null = null;
  private previousChildTileIds: Set<TileId> = new Set();

  drawerOpen: boolean = false;
  tileMenuOpen: boolean = false;
  tileMenuLoading: boolean = false;
  editingTile = false;
  private chatPermissionChecked: boolean = false;
  private fgaChildIds: TileId[] | null = null;
  private refreshChildren$ = new Subject<void>();
  tileMenuTitle = 'Tile Features';
  tileMenuOptions: OptionGroup = new OptionGroup([{
    id: TileMenuOptionId.CONFIGURE,
    name: 'Configure',
    callback: () => {
      this.editTile();
    },
  }]);

  isPopoverTile: boolean;
  popoverRawTile: Tile;
  childTiles: Tile[] = [];

  popoverPositions: ConnectedPosition[] = [{
    originX: 'center',
    originY: 'center',
    overlayX: 'center',
    overlayY: 'center',
  }];
  isLoading: boolean = true;
  isScrollPresent: boolean = false;


  constructor(
    protected tilesService: TilesService,
    protected shadeTools: ShadeToolsService,
    protected alarmsService: AlarmsService,
    protected tileHistoryService: TileHistoryService,
    protected permissionsService: PermissionsService,
    protected session: SessionService,
    private motiChatService: MotiChatService,
    private onDestroy$: NgDestroyService,
    private cdr: ChangeDetectorRef,
  ) {
  }


  openTileMenu() {
    this.tileMenuOpen = true;
    this.checkChatPermission();
  }

  get displayedMenuOptions(): OptionGroup {
    if (this.tileMenuLoading) {
      return new OptionGroup([{
        id: 'loading',
        name: '',
        icon: 'fa-regular fa-spin fa-loader',
      }]);
    }
    return this.tileMenuOptions;
  }

  private checkChatPermission() {
    if (this.chatPermissionChecked) return;

    this.chatPermissionChecked = true;
    this.tileMenuLoading = true;

    this.permissionsService.queryPermissions({
      UserID: this.session.userId,
      ObjectID: this.tile.ID,
      ObjectType: ObjectType.TILE,
      Permissions: [Permission.CAN_CHAT, Permission.CAN_CONFIGURE]
    }).subscribe({
      next: (response) => {
        const canChat = response.Results.find(r => r.Permission === Permission.CAN_CHAT)?.Allowed;
        const canConfigure = response.Results.find(r => r.Permission === Permission.CAN_CONFIGURE)?.Allowed;

        // Add chat option if permitted
        if (canChat && !this.tileMenuOptions.options.find(o => o.id === TileMenuOptionId.CHAT)) {
          this.tileMenuOptions.options.push({
            id: TileMenuOptionId.CHAT,
            name: 'Chat',
            icon: 'fa-kit fa-chat',
            callback: () => this.motiChatService.openChat(this.tile.ID, this.tile.displayName || this.tile.Name, ObjectType.TILE),
          });
        }

        // Remove configure option if not permitted
        if (!canConfigure) {
          this.tileMenuOptions.options = this.tileMenuOptions.options.filter(o => o.id !== TileMenuOptionId.CONFIGURE);
        }

        this.tileMenuLoading = false;
      },
      error: () => this.tileMenuLoading = false
    });
  }

  editTile() {
    this.tilesService.canConfigureTile$(this.tile.ID).subscribe({
      next: (canConfigure) => {
        this.editingTile = canConfigure;
        if (!canConfigure) {
          this.shadeTools.errorDialog(`You don't have permissions to configure this folder`);
        }
      },
      error: () => {
        this.editingTile = false;
        this.shadeTools.errorDialog(`You don't have permissions to configure this folder`);
      }
    });
  }


  get structure(): SuperFolderStructure {
    return this.tile.Structure as SuperFolderStructure;
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['matchingTileIds'] && this.isPopoverTile && this.popoverRawTile
      && !this.dataPointTiles.some(t => t.ID === this.popoverRawTile.ID)) {
      this.isPopoverTile = false;
    }
  }


  ngOnInit(): void {
    this.isLoading = true;

    // Subscribe to alarms immediately for faster badge display
    this.subscribeToAlarmsEvents();

    // Fetch FGA children (source of truth for parent-child relationships)
    // FGA determines WHICH tiles are children, excluding deleted tiles
    const fgaChildren$ = this.refreshChildren$.pipe(
      startWith(undefined),
      switchMap(() => this.tilesService.fetchTilesAndDescendants$([this.tile.ID]).pipe(
        map(tiles => tiles.filter(t => t.ID !== this.tile.ID).map(t => t.ID)),
        catchError(err => {
          console.error('Failed to fetch FGA children, showing empty folder:', err);
          return of([] as TileId[]);
        }),
        startWith(null as TileId[] | null),
      )),
      takeUntil(this.onDestroy$)
    );

    // Combine both streams - emits whenever either updates
    combineLatest([
      this.tilesService.sessUserTiles$,
      fgaChildren$
    ]).pipe(
      takeUntil(this.onDestroy$),
      map(([tileIdMap, fgaChildIds]) => {
        const tiles = Object.values(tileIdMap);
        const currentTile = tiles.find(tile => tile.ID === this.tile.ID);
        const structure = currentTile?.Structure as SuperFolderStructure;

        // FGA children = source of truth for relationships (which tiles are children)
        // Structure.TileIDs = user-defined ordering (how to display children)
        // Use FGA if available, otherwise fall back to Structure.TileIDs
        const childIdsToShow = fgaChildIds ?? structure?.TileIDs;

        return {
          tiles: tiles.filter(tile => childIdsToShow?.includes(tile.ID)),
          currentTile,
          structure,
          childIdsToShow,
          tileIdMap,
          fgaLoaded: fgaChildIds !== null
        };
      })
    ).subscribe({
      next: ({ tiles, currentTile, structure, childIdsToShow, tileIdMap, fgaLoaded }) => {
        // Update component state
        this.tile = currentTile;
        this.childTiles = this.sortChildTilesByOrder(tiles);

        if (fgaLoaded && childIdsToShow) {
          this.fgaChildIds = childIdsToShow;
        }

        const expectedCount = this.getExpectedTileCount(childIdsToShow, tileIdMap, structure);
        this.isLoading = !fgaLoaded || tiles.length < expectedCount;

        // Only recalculate alarm badge if child tile IDs changed
        if (this.childTileIdsChanged()) {
          this.updateAlarmBadge();
        }

        requestAnimationFrame(() => {
          this.updateScrollPresence();
        });
      }
    });
  }

  private childTileIdsChanged(): boolean {
    const currentIds = new Set(this.childTiles.map(t => t.ID));

    if (currentIds.size !== this.previousChildTileIds.size ||
        ![...currentIds].every(id => this.previousChildTileIds.has(id))) {
      this.previousChildTileIds = currentIds;
      return true;
    }

    return false;
  }

  updateScrollPresence(): void {
    if (!this.isDataPointsView) {
      this.isScrollPresent = false;
      return;
    }

    const element = this.dataPointsScrollableBoxElRef?.nativeElement;
    if (element) {
      this.isScrollPresent = element.scrollHeight > element.offsetHeight;
    }
  }


  closeDrawer() {
    this.drawerOpen = false;
  }


  refreshChildren() {
    this.refreshChildren$.next();
  }


  /**
   * Sorts child tiles using Structure.TileIDs for ordering.
   *
   * Important: FGA determines WHICH tiles are children (source of truth).
   * Structure.TileIDs only determines display ORDER.
   *
   * - Tiles in Structure.TileIDs appear first, in that order
   * - FGA children not in Structure.TileIDs are appended at the end
   */
  private sortChildTilesByOrder(unsortedTiles: Tile[]): Tile[] {
    const tileIdMap: TileIdMap = {};

    unsortedTiles.map(
      (t) => {
        tileIdMap[t.ID] = t;
      },
    );

    const sortedTiles = [];

    // First, add tiles in Structure.TileIDs order (user-defined ordering)
    for (const tileId of this.structure.TileIDs || []) {
      if (tileIdMap[tileId]) {
        sortedTiles.push(tileIdMap[tileId]);
      }
    }

    // Then append FGA children not in Structure.TileIDs (newly added via bulk creation, etc.)
    unsortedTiles.forEach(tile => {
      if (!this.structure.TileIDs?.includes(tile.ID)) {
        sortedTiles.push(tile);
      }
    });

    return sortedTiles;
  }


  getTileDef(tile: Tile): TileMinimalElemSetup {
    const elemOptions: TileElemOptions = {
      tile,
      displayColor: isValidTileColor(tile.Appearance.Color),
      displayIcon: tile.Appearance.Icon,
      displayName: tile.displayName,
    };


    return new TileMinimalElemSetup(elemOptions);
  }


  private subscribeToAlarmsEvents(): void {
    this.alarmsService.activeEvents$.pipe(
      takeUntil(this.onDestroy$),
    ).subscribe(
      (events) => {
        this.currentAlarmEvents = events;
        this.updateAlarmBadge();
      },
    );
  }

  private updateAlarmBadge(): void {
    if (!this.currentAlarmEvents) {
      this.alarmEvent = undefined;
      return;
    }

    const childTilesIDs = this.childTiles.map(tile => tile.ID);
    const childTileEvents = this.currentAlarmEvents.filter(
      e => childTilesIDs.includes(e.ObjectID) && e.ObjectType === ObjectType.TILE
    );

    const nonAckEvent = childTileEvents.find(event => event.AckTime === null);
    const anyAckEvent = childTileEvents.find(event => event.AckTime !== null);

    // Show unacknowledged badge first, then acknowledged, then nothing
    this.alarmEvent = nonAckEvent || anyAckEvent;
  }


  get facetValues(): string[] {
    return this.tile?.facetValues ?? [];
  }


  get isDefaultView(): boolean {
    return !this.structure.DisplayMode || this.structure.DisplayMode === TileFolderDisplayMode.Default;
  }


  get isDataPointsView(): boolean {
    return this.structure.DisplayMode === TileFolderDisplayMode.DataPoints;
  }


  get matchingChildTiles(): Tile[] {
    return this.applySearchFilter(this.childTiles);
  }


  get dataPointTiles(): Tile[] {
    const tiles = !!this.structure.DataPoints ? this.visibleDataPointTiles : this.childTiles;
    return this.applySearchFilter(tiles);
  }


  private applySearchFilter(tiles: Tile[]): Tile[] {
    return filterByMatchingIds(tiles, tile => tile.ID, this.matchingTileIds);
  }


  get visibleDataPointTiles(): Tile[] {
    return [...this.childTiles].filter(tile =>
      !this.structure.DataPoints.find(dataPoint =>
        dataPoint.TileID === tile.ID,
      )?.Hidden,
    );
  }


  get dataPointsFolderHeight(): number {
    return this.tile.Appearance.Dimensions?.Height?.Quantity * DEFAULT_DATA_POINT_FOLDER_VIEW_ITEM_PX_HEIGHT;
  }


  scrollTileUp(): void {
    this.dataPointsScrollableBoxElRef.nativeElement.scrollBy(0, -DEFAULT_DATA_POINT_FOLDER_VIEW_ITEM_PX_HEIGHT);
    this.updateScrollPresence();
  }

  scrollTileDown(): void {
    this.dataPointsScrollableBoxElRef.nativeElement.scrollBy(0, DEFAULT_DATA_POINT_FOLDER_VIEW_ITEM_PX_HEIGHT);
    this.updateScrollPresence();
  }


  /**
   * Returns expected tile count based on child IDs and tile map.
   * Excludes deleted tiles (not in tileMap).
   */
  private getExpectedTileCount(childIds: TileId[], tileMap: TileIdMap, structure?: SuperFolderStructure): number {
    if (!childIds || childIds.length === 0) {
      return 0;
    }

    // Filter out deleted tiles
    const nonDeletedTileIds = childIds.filter(tileId => tileMap[tileId] !== undefined);

    // Use provided structure or fall back to this.structure for backwards compatibility
    const targetStructure = structure ?? this.structure;

    if (targetStructure?.DisplayMode === TileFolderDisplayMode.DataPoints && targetStructure.DataPoints) {
      // Count only non-hidden tiles
      return nonDeletedTileIds.filter(tileId =>
        !targetStructure.DataPoints.find(dp => dp.TileID === tileId)?.Hidden
      ).length;
    }

    return nonDeletedTileIds.length;
  }

  /**
   * Returns expected tile count using live sessUserTiles.
   * Used by dataPointsViewMinHeight getter.
   */
  get expectedTileCount(): number {
    const childIds = this.fgaChildIds ?? (this.structure?.TileIDs || []);
    const filteredChildIds = filterByMatchingIds(childIds, id => id, this.matchingTileIds);
    return this.getExpectedTileCount(filteredChildIds, this.tilesService.sessUserTiles, this.structure);
  }

  get dataPointsViewMinHeight(): string {
    // Calculate height based on expected tiles and user-configured height
    const expectedHeight = this.expectedTileCount * DEFAULT_DATA_POINT_FOLDER_VIEW_ITEM_PX_HEIGHT;

    // If user has set a custom height, use that as the maximum
    if (this.tile?.Appearance?.Dimensions?.Height?.Quantity) {
      const userConfiguredHeight = this.dataPointsFolderHeight;
      return `${Math.min(expectedHeight, userConfiguredHeight)}px`;
    }

    // Otherwise use the calculated height
    return `${expectedHeight}px`;
  }


  onTogglePopoverTile(rawTile: Tile) {
    this.isPopoverTile = !this.isPopoverTile;
    this.popoverRawTile = rawTile;
  }


  get popoverTilePositionHeight(): number {
    const tileToDisplayNumber = this.dataPointTiles.findIndex(tile => (tile.ID === this.popoverRawTile.ID)) + 1;
    // 150 is approximate height of popoverTile
    const POPOVER_HALF_HEIGHT = 75;

    // we need to position popover tile in the middle of the dataPoint that it was invoked from
    return (tileToDisplayNumber * DEFAULT_DATA_POINT_FOLDER_VIEW_ITEM_PX_HEIGHT) - this.dataPointsScrollableBoxElRef.nativeElement.scrollTop - POPOVER_HALF_HEIGHT;
  }


  get displayIcon(): TileIcon {
    return this.tile.Appearance.Icon;
  }
}
